import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Loader2, Download, Users, Eye, UserCheck, CalendarClock, FileText, Clock, CheckCircle2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const PIE_COLORS = ["#F5842B", "#2E9BD6", "#E23B2E", "#F5B942", "#7C5CFC", "#3ECf8E", "#EC4899", "#22D3EE", "#A3E635", "#94a3b8"];

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [countries, setCountries] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) { navigate("/", { replace: true }); return; }
    if (user?.is_admin) {
      Promise.all([
        api.get("/admin/purchasers").then((r) => setData(r.data)).catch(() => setData({ purchasers: [], total: 0 })),
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null)),
        api.get("/admin/visits/daily").then((r) => setDaily((r.data.days || []).map((d) => ({ ...d, label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}` })))).catch(() => setDaily([])),
        api.get("/admin/visits/countries").then((r) => setCountries(r.data.countries || [])).catch(() => setCountries([])),
        api.get("/admin/courses/files").then((r) => setFiles(r.data.courses || [])).catch(() => setFiles([])),
      ]).finally(() => setLoading(false));
    }
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5842B]" /></div>;
  }

  const rows = data?.purchasers || [];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Navbar />
      <section className="pt-28 pb-16 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90 flex items-center gap-2"><Users size={14} /> {t("admin.title")}</span>
            <h1 className="font-display text-3xl font-extrabold mt-2">{t("admin.subtitle")}</h1>
            <p className="text-slate-400 text-sm mt-2">{t("admin.total")}: <span className="text-white font-semibold" data-testid="admin-total">{data?.total || 0}</span></p>
            <p className="text-slate-400 text-sm mt-1">{t("admin.revenue")}: <span className="text-[#F5842B] font-bold text-lg" data-testid="admin-revenue">€{((data?.total_revenue_cents || 0) / 100).toFixed(2)}</span></p>
          </div>
          <a href={`${API}/admin/purchasers.csv`} className="adj-gradient text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0" data-testid="admin-export-csv">
            <Download size={17} /> {t("admin.export")}
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="glass rounded-2xl p-6 flex items-center gap-4" data-testid="stat-total-views">
            <div className="w-12 h-12 rounded-xl bg-[#F5842B]/15 flex items-center justify-center text-[#F5842B]"><Eye size={22} /></div>
            <div>
              <div className="font-display text-3xl font-extrabold">{stats?.total_views ?? 0}</div>
              <div className="text-slate-400 text-xs uppercase tracking-wide font-mono mt-1">{t("admin.visits")}</div>
            </div>
          </div>
          <div className="glass rounded-2xl p-6 flex items-center gap-4" data-testid="stat-unique-visitors">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400"><UserCheck size={22} /></div>
            <div>
              <div className="font-display text-3xl font-extrabold">{stats?.unique_visitors ?? 0}</div>
              <div className="text-slate-400 text-xs uppercase tracking-wide font-mono mt-1">{t("admin.uniqueVisitors")}</div>
            </div>
          </div>
          <div className="glass rounded-2xl p-6 flex items-center gap-4" data-testid="stat-today-views">
            <div className="w-12 h-12 rounded-xl bg-sky-500/15 flex items-center justify-center text-sky-400"><CalendarClock size={22} /></div>
            <div>
              <div className="font-display text-3xl font-extrabold">{stats?.today_views ?? 0}</div>
              <div className="text-slate-400 text-xs uppercase tracking-wide font-mono mt-1">{t("admin.todayVisits")}</div>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 mb-10" data-testid="admin-visits-chart">
          <h3 className="font-display text-lg font-bold mb-4">{t("admin.chartTitle")}</h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F5842B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F5842B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} interval="preserveStartEnd" minTickGap={20} />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} width={36} />
                <Tooltip contentStyle={{ background: "#121721", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} labelStyle={{ color: "#94a3b8" }} />
                <Area type="monotone" dataKey="count" stroke="#F5842B" strokeWidth={2} fill="url(#visGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 mb-10" data-testid="admin-countries-chart">
          <h3 className="font-display text-lg font-bold mb-4">{t("admin.countriesTitle")}</h3>
          {countries.length === 0 ? (
            <p className="text-slate-400 text-sm" data-testid="admin-countries-empty">{t("admin.countriesEmpty")}</p>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={countries} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} labelLine={false} label={(e) => `${e.name} (${e.count})`}>
                    {countries.map((c, i) => (
                      <Cell key={c.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#121721", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6 mb-10" data-testid="admin-files">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><FileText size={18} className="text-[#F5842B]" /> {t("admin.filesTitle")}</h3>
          <div className="space-y-2">
            {files.map((f) => (
              <div key={f.slug} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-testid={`admin-file-${f.slug}`}>
                <span className="font-mono text-[10px] px-2 py-1 rounded bg-white/10 text-slate-300 shrink-0 w-fit">{f.code}</span>
                <span className="font-medium text-white flex-1">{f.title}</span>
                <div className="flex flex-wrap items-center gap-2">
                  {["it", "en", "es", "de"].map((lg) => (
                    <span key={lg} data-testid={`admin-file-${f.slug}-${lg}`}
                      className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${f.langs?.[lg] ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/40 bg-amber-500/10"}`}
                      title={f.langs?.[lg] ? t("admin.fileAvailable") : t("admin.fileMissing")}>
                      {f.langs?.[lg] ? <CheckCircle2 size={12} /> : <Clock size={12} />} {lg.toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-3">
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-300" /> {t("admin.fileAvailable")}</span>
            <span className="flex items-center gap-1"><Clock size={12} className="text-amber-300" /> {t("admin.fileMissing")}</span>
          </p>
        </div>

        {rows.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-slate-400" data-testid="admin-empty">{t("admin.empty")}</div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden overflow-x-auto" data-testid="admin-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 font-mono text-xs border-b border-white/10">
                  <th className="px-5 py-4">{t("admin.colDate")}</th>
                  <th className="px-5 py-4">{t("admin.colName")}</th>
                  <th className="px-5 py-4">{t("admin.colEmail")}</th>
                  <th className="px-5 py-4">{t("admin.colPhone")}</th>
                  <th className="px-5 py-4">{t("admin.colProduct")}</th>
                  <th className="px-5 py-4 text-right">{t("admin.colAmount")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-row-${i}`}>
                    <td className="px-5 py-4 text-slate-400 whitespace-nowrap">{(r.date || "").slice(0, 10)}</td>
                    <td className="px-5 py-4 text-white">{`${r.first_name} ${r.last_name}`.trim() || r.name || "—"}</td>
                    <td className="px-5 py-4 text-slate-300">{r.email}</td>
                    <td className="px-5 py-4 text-slate-300">{r.phone || "—"}</td>
                    <td className="px-5 py-4"><span className="text-[#F5842B]">{r.product}</span></td>
                    <td className="px-5 py-4 text-right text-white font-semibold whitespace-nowrap" data-testid={`admin-amount-${i}`}>€{((r.amount_cents || 0) / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
